import { useGetAllUrlFilters } from 'modules/messagingInsights/hooks/useGetAllUrlFilters';
import { SavedViewParams } from 'modules/messagingInsights/types';
import { ListedView } from 'modules/savedViewsIndex/types';
import { compileSharedUrl } from 'modules/messagingInsights/utils';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MakeCopy16, Confirm16 } from 'ui/icons';
import { WarningBanner } from 'ui/banner';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Modal, ModalBody, ModalHeader, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { Caption, colors, spacing, body2Bold, easing, duration, monospace2 } from 'ui/theme';
import { addNegativeToast } from 'modules/toastContainer';

const Error = styled.div`
  margin-bottom: ${spacing.half};
`;

const Item = styled.li`
  display: list-item;
  list-style-type: disc;
`;

const UrlWrapper = styled.div`
  align-items: center;
  background: ${colors.backgroundTertiary};
  border-radius: ${spacing.quarter};
  border: ${spacing.single} solid ${colors.grey15};
  display: flex;
  margin-bottom: ${spacing.half};
  padding: ${spacing.quarter} ${spacing.quarter} ${spacing.quarter} 0;
  cursor: pointer;
  transition: all ${easing.exit} ${duration.short};

  &:hover {
    border-color: ${colors.borderInteractive};
    transition: all ${easing.entrance} ${duration.short};
  }
`;

const Url = styled.div`
  ${monospace2}
  color: ${colors.textSecondary};
  margin-left: ${spacing.threeQuarter};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const ModalContent = styled.div`
  ${body2Bold}
  margin-bottom: ${spacing.half}
`;

const StyledCaption = styled(Caption)`
  color: ${colors.textSecondary};
`;

export const copy = {
  negativeMsg: 'Could not copy url to clipboard. Please try again.',
  title: 'Share filters',
  viewTitle: 'Share view',
  subTitle: 'Share your selected filters and date range',
  viewSubtitle: 'Share your view',
  subtext: 'Only users in your Iterable project can view this link.',
  copied: 'Copied!',
  toCopy: 'Copy',
  urlTooLong:
    'Too many filters are currently selected. The following were excluded from the share URL:',
  privateWarning:
    'This view is private. Using this link, only the selected filters and date range will be shared.',
};

type Props = {
  view: ListedView | SavedViewParams;
  onClose: () => void;
};

export const ShareViewModal = ({ view, onClose }: Props) => {
  const [copied, setCopied] = useState(false);
  const dispatch = useDispatch();

  // Link preparation
  const allFilters = useGetAllUrlFilters();
  const { completeUrl: filterUrl, error } = compileSharedUrl(allFilters);

  const isPublicView = view && !view.isPrivate;

  // Only share a view ID (and not the filter-based url)
  const shareLink = isPublicView
    ? `${window.location.origin}/messagingInsights/views/${view.id}`
    : filterUrl;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    } catch (err) {
      setCopied(false);
      dispatch(addNegativeToast(copy.negativeMsg));
    }
  };

  // url max length error
  useEffect(() => {
    if (error.length > 0) {
      onClose();
      dispatch(
        addNegativeToast(
          <>
            <Error>{copy.urlTooLong}</Error>
            <ul>
              {error.map((e, i) => (
                <Item key={i}>{e}</Item>
              ))}
            </ul>
          </>,
        ),
      );
    }
  }, []);

  return (
    <Modal
      id="share-modal"
      onClose={onClose}
      isModalOpen={true}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{isPublicView ? copy.viewTitle : copy.title}</ModalHeader>
      <ModalBody>
        {view && view.isPrivate && <WarningBanner>{copy.privateWarning}</WarningBanner>}
        <ModalContent>{isPublicView ? copy.viewSubtitle : copy.subTitle}</ModalContent>
        <UrlWrapper onClick={handleCopy}>
          <Url data-test="share-url">{shareLink}</Url>
          <Button onClick={handleCopy} size={BUTTON_SIZES.SMALL} variant={BUTTON_VARIANTS.PRIMARY}>
            {copied ? <Confirm16 /> : <MakeCopy16 />}
            {copied ? copy.copied : copy.toCopy}
          </Button>
        </UrlWrapper>
        <StyledCaption data-test="caption">{copy.subtext}</StyledCaption>
      </ModalBody>
      <ModalFooter hasCancel={false}></ModalFooter>
    </Modal>
  );
};
